import {
  Menu as ChakraMenu,
  MenuButton,
  Flex,
  Text,
  MenuList,
  MenuItem,
  useOutsideClick,
} from '@chakra-ui/react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 2-level menu. Can be moved
 * to shared components if necessary.
 */
export const Menu = ({
  options = [],
  value: propValue,
  prefix,
  buttonProps = {},
  onChange = () => {},
  error = false,
  placeholder,
}) => {
  /**
   * Does not change internal state if
   * "propValue" is changed, so it will act
   * more like "defaultValue"
   */
  const [value, setValue] = useState();
  useEffect(() => {
    setValue(propValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue?.id]);

  const [open, setOpen] = useState(false);
  const [subMenuOpen, setSubMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => {
    if (open) {
      setOpen(false);
      setSubMenuOpen(false);
    } else {
      setOpen(true);
    }
  }, [open]);

  const selectValue = useCallback(
    (value) => {
      setValue(value);
      toggleMenu();
    },
    [toggleMenu]
  );

  useEffect(() => {
    if (value) {
      onChange?.(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const triggerRef = useRef();
  const menuListRef = useRef();

  useOutsideClick({
    enabled: open,
    ref: menuListRef,
    handler: (event) => {
      // Prevent triggering "toggleMenu" multiple times
      // as "onClick" will trigger it for the button.
      if (event.target === triggerRef.current) {
        return;
      }
      if (!menuListRef?.current?.contains(event.target)) {
        toggleMenu();
      }
    },
  });

  return (
    <ChakraMenu isOpen={open} autoSelect={false} closeOnBlur={false}>
      <MenuButton
        cursor="pointer"
        backgroundColor="white"
        borderRadius="8px"
        border="1px solid #D0D5DD"
        padding="8px"
        maxWidth="200px"
        // Necessary because not specifying a "role"
        // will submit a form when menu is opened
        type="button"
        {...buttonProps}
        onClick={toggleMenu}
        ref={triggerRef}
      >
        <Flex
          flexDirection="row"
          alignItems="center"
          columnGap="4px"
          flexWrap="nowrap"
        >
          {!value && placeholder ? (
            <Text color="hsl(0, 0%, 50%)">{placeholder}</Text>
          ) : (
            <Text
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              fontSize="14px"
            >
              {prefix ? (
                <>
                  {prefix} <strong>{value?.name}</strong>
                </>
              ) : (
                value?.name
              )}
            </Text>
          )}
          <ChevronDownIcon
            width="16px"
            style={{
              display: 'inline',
              marginLeft: 'auto',
              marginBottom: '2px',
            }}
          />
        </Flex>
      </MenuButton>
      <MenuList ref={menuListRef}>
        {options.map((option, inx) => {
          const { name, options = [] } = option;
          if (options.length) {
            return (
              <ChakraMenu
                placement="left-start"
                isOpen={subMenuOpen === inx}
                key={`menu-item-${inx}`}
              >
                <MenuButton
                  _hover={{ backgroundColor: '#F9FAFB' }}
                  _focus={{ backgroundColor: '#F9FAFB' }}
                  as={MenuItem}
                  onClick={() => setSubMenuOpen(inx)}
                >
                  <Flex alignItems="center">
                    {name}{' '}
                    <ChevronRightIcon
                      style={{ marginLeft: 'auto' }}
                      width="16px"
                    />
                  </Flex>
                </MenuButton>
                <MenuList>
                  {options.map((option, subMenuInx) => (
                    <MenuItem
                      onClick={() => selectValue(option)}
                      key={`menu-${inx}-item-${subMenuInx}`}
                      _hover={{ backgroundColor: '#F9FAFB' }}
                      _focus={{ backgroundColor: '#F9FAFB' }}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </ChakraMenu>
            );
          }
          return (
            <MenuItem
              key={`menu-item-${inx}`}
              onClick={() => selectValue(option)}
              _hover={{ backgroundColor: '#F9FAFB' }}
              _focus={{ backgroundColor: '#F9FAFB' }}
            >
              {name}
            </MenuItem>
          );
        })}
      </MenuList>
    </ChakraMenu>
  );
};
